import { Formik } from "formik";
import { injectIntl } from "react-intl";

import {
  isEmpty,
  SubmitButton,
  TextInput,
  intlShape,
  Form,
  ActionBar,
  Row,
  Column,
  ButtonGroup,
  apiShape,
  withSession,
  sessionShape,
  notifierShape,
  withNotifier,
  withApi,
  withBackend,
  backendShape,
} from "lcm-iot-commons";

import PasswordInput from "../Form/PasswordInput";
import withZxcvbn, { zxcvbnShape } from "../../wrappers/withZxcvbn";

type OwnProps = {
  intl: intlShape;
  api: apiShape;
  notifier: notifierShape;
  zxcvbn: zxcvbnShape;
  session: sessionShape;
  backend: backendShape;
  userDetails?: {
    email?: string;
    first_name?: string;
    last_name?: string;
  };
};

const defaultProps: Partial<OwnProps> = {
  userDetails: {},
};

type Props = OwnProps & typeof defaultProps;

export function UserPasswordEdit({
  intl,
  api,
  notifier,
  zxcvbn,
  session,
  backend,
  userDetails,
}: Props) {
  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const updateObject = {
        current_password: values.password,
        new_password: values.newPassword,
      };
      await api.patch("/users/current/password", updateObject);
      notifier.showSuccess(
        intl.formatMessage({ id: "edit_password.success_notification" }),
      );
      await backend.delete("/invalidate_current_user_sessions");
      (session as $TSFixMe).deleteSession();
    } catch (error) {
      if ((error as $TSFixMe).contains("invalid_password")) {
        notifier.showError(
          intl.formatMessage({ id: "api.error.invalid_password" }),
        );
      } else {
        notifier.showError(api.translateError(error));
      }
    } finally {
      actions.setSubmitting(false);
      actions.resetForm({ values: {} });
    }
  };

  const validateForm = (values: $TSFixMe) => {
    const errors = {};
    if (isEmpty(values.password)) {
      (errors as $TSFixMe).password = intl.formatMessage({
        id: "validation.password.mandatory",
      });
    }
    if (isEmpty(values.newPassword)) {
      (errors as $TSFixMe).newPassword = intl.formatMessage({
        id: "validation.password.mandatory",
      });
    } else {
      const userInputs = [
        userDetails?.email || "",
        userDetails?.first_name || "",
        userDetails?.last_name || "",
      ];
      const passwordError = zxcvbn.checkPassword(
        values.newPassword,
        userInputs,
      ).error;
      if (passwordError) {
        (errors as $TSFixMe).newPassword = passwordError;
      }
    }
    return errors;
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting } = formProps;
    return (
      <Form {...formProps}>
        <Row id="user-profile-password">
          <Column md={8}>
            <TextInput
              {...formProps}
              id="user-profile-current-password"
              component={TextInput}
              label={intl.formatMessage({ id: "label.current_password" })}
              intl={intl}
              name="password"
              disabled={isSubmitting}
              type="password"
              required
            />
          </Column>
        </Row>
        <Row id="user-profile-new-password">
          <Column md={8}>
            <PasswordInput
              {...formProps}
              label={intl.formatMessage({ id: "label.new_password" })}
              intl={intl}
              name="newPassword"
              type="password"
              disabled={isSubmitting}
              required
              userDetails={userDetails}
            />
          </Column>
        </Row>
        <div data-testid="user-profile-note">
          Note: All existing sessions will be terminated after password change
        </div>
        {/* @ts-expect-error TS(2322) FIXME: Type '{ children: Element; className: string; }' i... Remove this comment to see the full error message */}
        <ButtonGroup className="btn-group">
          <SubmitButton
            id="edit-password-submit"
            fetching={isSubmitting}
            disabled={!formProps.dirty}
            text={intl.formatMessage({ id: "change_current_password.submit" })}
          />
        </ButtonGroup>
      </Form>
    );
  };

  return (
    <div className="form-group">
      <ActionBar>
        <h2 id="change-current-password-header">
          {intl.formatMessage({ id: "change_current_password.header" })}
        </h2>
      </ActionBar>
      <Formik
        id="edit-password-form"
        onSubmit={handleOnSubmit}
        validate={validateForm}
        initialValues={{}}
      >
        {renderForm}
      </Formik>
    </div>
  );
}

UserPasswordEdit.defaultProps = defaultProps;

export default injectIntl(
  // @ts-expect-error TS(2769) FIXME: No overload matches this call.
  withZxcvbn(withSession(withBackend(withNotifier(withApi(UserPasswordEdit))))),
);
